import { useState } from 'react'
import gql from 'graphql-tag'
import { NetworkStatus, useQuery } from '@apollo/client'
import { useParams, useHistory } from 'react-router-dom'
import Cookies from 'js-cookie'
import { get } from 'lodash'

import fragments from 'modules/contribution/graphqlFragments'
import { GiftingSkeleton } from '../components/gifting-page/GiftingSkeleton'
import GiftingPage from '../components/gifting-page/GiftingPage'
import { getQuery } from 'hooks/getQuery'
import {
  currentFlowValue,
  frequencyValue,
  referrerValue
} from 'modules/contribution/LocalState'
import { buildPath } from 'routes'
import { FrequencyGifter } from '../types'
import useApolloCacheQuery from 'hooks/useApolloCacheQuery'
import { FundBacker } from 'modules/fund/components/FundBackers'
import { PartnerName } from 'modules/profile/userInterfaces'
import { ContributionLimits } from 'modules/contribution/types'

const GIFTING_PAGE = gql`
  query GiftingPage($input: GiftingPageInputType!) {
    giftingPage(input: $input) {
      id
      fund {
        id
        uuid
        handle
        programManager
        canUpdateFund
        collegeEntryYear
        countBackers
        childAgeLabel
        fundName
        yearsUntilCollege
        isBacker

        fundOwner {
          id
          firstName
          fullName
          lastName
          partnerName
          uuid
        }

        giftingPage {
          id
          ownerPhotoUrl
          photoUrl
          greeting
          currentUser {
            id
          }
        }

        contributionLimits {
          minimum
          maximum
          minimumMonthly
          maximumMonthly
          maximumAnnually
        }
      }
    }
  }
`

const CURRENT_USER = gql`
  query CurrentUser {
    currentUser {
      ...ContributionUserFragment
    }
  }
  ${fragments.user}
`

const GiftingPageContainer = ({ handleProp }: { handleProp?: string }) => {
  const { handle: handleParam }: { handle: string } = useParams()
  const handle = handleParam || handleProp
  const history = useHistory()
  const [defaultFrequency, setDefaultFrequency] =
    useState<FrequencyGifter>('onetime')
  const queryParams = getQuery()
  const showSetupModal = queryParams.get('setup') === 'true'
  const frequency: any = queryParams.get('frequency')

  const variables = { input: { handle } }
  const { error, data, loading } = useQuery(GIFTING_PAGE, {
    variables,
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (frequency) {
        setDefaultFrequency(frequency)
        frequencyValue(frequency)
      } else {
        const fund: Fund = get(data, 'giftingPage.fund')
        const isAuthenticated = !!Cookies.get('authorization')

        if (isAuthenticated && fund.canUpdateFund) {
          setDefaultFrequency('monthly')
          frequencyValue('monthly')
          currentFlowValue('create-contribution')
        } else {
          setDefaultFrequency('onetime')
          frequencyValue('onetime')
          currentFlowValue('create-gift')
        }
      }
      referrerValue('giftingPage')
    },
    onError: (error) => {
      console.error(error)
      history.replace(buildPath('notFound'))
    }
  })
  const { complete } = useApolloCacheQuery(GIFTING_PAGE, variables)

  const {
    loading: userLoading,
    data: userData,
    error: userError,
    refetch: refetchCurrentUser,
    networkStatus
  } = useQuery(CURRENT_USER, {
    fetchPolicy: 'cache-and-network'
  })

  const { complete: userComplete } = useApolloCacheQuery(CURRENT_USER)
  if (
    ((loading && !complete) || (userLoading && !userComplete)) &&
    networkStatus !== NetworkStatus.refetch
  ) {
    return <GiftingSkeleton />
  }
  if (error) return <>Error: {error.message}</>

  const user = userError ? null : get(userData, 'currentUser')

  const fund: Fund = data.giftingPage.fund
  return (
    <GiftingPage
      fund={fund}
      defaultFrequency={defaultFrequency}
      showSetupModal={showSetupModal}
      user={user}
      refetchCurrentUser={refetchCurrentUser}
    />
  )
}

interface Fund {
  id: string
  uuid: string
  handle: string
  programManager: string
  canUpdateFund: boolean
  countBackers: number
  childAgeLabel?: string
  yearsUntilCollege: number
  collegeEntryYear: number
  isBacker: boolean
  fundName: string
  fundOwner: {
    firstName: string
    fullName: string
    id: string
    partnerName: PartnerName
  }
  giftingPage: {
    ownerPhotoUrl: string
    photoUrl: string
    greeting: string
  }
  contributionLimits: ContributionLimits
}


export default GiftingPageContainer
